import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Controls the desktop header navigation.
 *
 * Supports:
 * - Top-level hover menus
 * - Digitek-style cascading submenus
 * - Overflow navigation
 * - Keyboard focus
 * - Escape-key closing
 * - Outside-click closing
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(),
      {
        signal: this.#abortController.signal,
      }
    );

    document.addEventListener(
      'pointerdown',
      this.#handleOutsidePointerDown,
      {
        signal: this.#abortController.signal,
      }
    );

    document.addEventListener(
      'keydown',
      this.#handleKeydown,
      {
        signal: this.#abortController.signal,
      }
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Closing animation duration supplied by the Liquid block.
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;

    return value ? Number.parseInt(value, 10) : 0;
  }

  /**
   * Returns the overflow menu rendered inside its shadow root.
   */
  get overflowMenu() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      ) ?? null
    );
  }

  /**
   * Checks whether the overflow menu is currently hovered.
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Called by the Liquid event attributes:
   *
   * on:focus="/activate"
   * on:pointerenter="/activate"
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Opens the submenu associated with the hovered/focused item.
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const menuItemContainer = item.closest(
      '.menu-list__list-item'
    );

    const isOverflowItem =
      menuItemContainer?.slot === 'overflow';

    this.dataset.overflowExpanded =
      isOverflowItem.toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    const overflowMenuHeight =
      this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && isOverflowItem) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu
      ? Math.max(
          submenu.offsetHeight,
          overflowMenuHeight
        )
      : 0;

    this.style.setProperty(
      '--submenu-height',
      `${submenuHeight}px`
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );
  };

  #debouncedActivateHandler = debounce(
    this.#activateHandler,
    ACTIVATE_DELAY
  );

  /**
   * Called by:
   *
   * on:blur="/deactivate"
   * on:pointerleave="/deactivate"
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Immediately closes the currently active submenu.
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (!item) return;

    if (item !== this.#state.activeItem) {
      return;
    }

    if (this.overflowHovered) {
      return;
    }

    this.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    window.setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(
    this.#deactivate,
    DEACTIVATE_DELAY
  );

  /**
   * Closes the menu when the user clicks outside the header.
   */
  #handleOutsidePointerDown = (event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (this.contains(event.target)) {
      return;
    }

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    this.#deactivate();
  };

  /**
   * Closes the active menu with the Escape key.
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();

    const activeItem =
      this.#state.activeItem;

    this.#deactivate();

    if (activeItem instanceof HTMLElement) {
      try {
        activeItem.focus({
          preventScroll: true,
        });
      } catch (error) {
        activeItem.focus();
      }
    }
  };

  /**
   * Preloads menu images when image-based menu styles are used.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Finds the top-level menu link belonging to an event target.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  const menuItemContainer = element.closest(
    '.menu-list__list-item'
  );

  if (!menuItemContainer) {
    return null;
  }

  /*
   * When the More button is hovered, use the first
   * item moved into the overflow slot.
   */
  if (menuItemContainer.matches('[slot="more"]')) {
    const overflowItem =
      menuItemContainer.parentElement?.querySelector(
        '[slot="overflow"]'
      );

    return findMenuItem(overflowItem);
  }

  const menuItem =
    menuItemContainer.querySelector(
      '[ref="menuitem"]'
    );

  return menuItem instanceof HTMLElement
    ? menuItem
    : null;
}

/**
 * Finds the submenu connected to a top-level link.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) {
    return null;
  }

  const menuItemContainer = element.closest(
    '.menu-list__list-item'
  );

  if (!menuItemContainer) {
    return null;
  }

  const submenu =
    menuItemContainer.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}